// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { appStore } from '@client-app/store';
import { advisoryApi } from '@shared/data-access/api/brokerage/advisory-api';
import { selectToken } from '@shared/data-access/slices/user';
import { RTK_PROMISE_TRACKER_ID } from '@shared/data-access/utils/constants';
import { isTest } from '@shared/environment';
import { trackPromise } from 'react-promise-tracker';
import { redirect, type LoaderFunction } from 'react-router';

/**
 * Ensures Silver Alpha is only reachable when the client has at least one advisory
 * proposal; otherwise redirects to aggregation.
 */
export const silverAlphaLoader: LoaderFunction = async () =>
  trackPromise(
    (async () => {
      await new Promise<void>(resolve => {
        if (isTest) {
          resolve();
          return;
        }

        const unsubscribe = appStore.subscribe(() => {
          const state = appStore.getState();

          if (state._persist?.rehydrated) {
            unsubscribe();
            resolve();
          }
        });

        const currentState = appStore.getState();

        if (currentState._persist?.rehydrated) {
          resolve();
        }
      });

      const state = appStore.getState();

      if (!selectToken(state)) {
        return { ok: true };
      }

      const proposalsResponse = await appStore.dispatch(
        advisoryApi.endpoints.getAdvisoryProposals.initiate(),
      );

      if (
        proposalsResponse.status === 'fulfilled' &&
        (proposalsResponse.data?.length ?? 0) === 0
      ) {
        return redirect('/aggregation');
      }

      return { ok: true };
    })(),
    RTK_PROMISE_TRACKER_ID,
  );
